/**
 * useTimeout
 *
 * A small hook that provides a self-clearing setTimeout utility:
 * - Clears any existing timeout before scheduling a new one
 * - Automatically clears on component unmount
 *
 * Usage:
 * const { schedule, clear } = useTimeout();
 * schedule(() => doSomething(), 300);
 */

import { useCallback, useEffect, useRef } from 'react';

type TimeoutHandle = ReturnType<typeof setTimeout> | null;

export const useTimeout = () => {
  const timeoutRef = useRef<TimeoutHandle>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback(
    (cb: () => void, delay: number) => {
      // Ensure any pending timeout is cleared before scheduling a new one
      clear();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        cb();
      }, delay);
    },
    [clear],
  );

  // Cleanup on unmount
  useEffect(() => clear, [clear]);

  return { schedule, clear };
};

export default useTimeout;
